import React from 'react';
import { MapPinIcon, PhoneIcon, EnvelopeIcon, ClockIcon } from '@heroicons/react/24/outline';
import content from '../data/all_content.json';
import SEO from '../components/SEO';

const About: React.FC = () => {
  const aboutData = content.pages.find((p: any) => p.slug === 'about-us');

  return (
    <div className="bg-white">
      <SEO 
        title="About Us - Plenco Plumbing & Construction Co. Ltd | Expert Team Serving Uganda Since 2013"
        description="Learn about Plenco Plumbing & Construction Co. Ltd - established in 2013, serving East Africa with expert construction, plumbing, and tank calibration services. Our professional team of 50+ skilled engineers and technicians delivers quality workmanship across Uganda. ISO certified and fully licensed."
        keywords="about Plenco, construction company Uganda history, plumbing company Kampala, established 2013, East Africa construction, professional plumbers Uganda, licensed construction company Kampala, experienced builders Uganda"
        canonicalUrl="http://www.plencoplumbingltd.com/about"
        type="website"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-700 text-white pt-32 sm:pt-36 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/10 px-4 py-1 rounded-full text-sm font-semibold mb-4">ABOUT PLENCO</div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            <span className="text-white">About</span> <span className="text-primary-200">Plenco</span>
          </h1>
          <p className="text-xl text-primary-100">Delivering excellence in construction and plumbing since 2013</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/images/gallery/PLENCO-MM-1.png" 
                alt="Plenco Team" 
                className="rounded-lg shadow-xl w-full"
              />
            </div>
            <div>
              <h2 className="text-4xl font-black text-gray-900 mb-6">
                <span className="text-primary">Who We</span> <span className="text-gray-600">Are</span>
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  We are a limited Liability Company based in Kampala, Uganda, specialized in construction,
                  plumbing, tank calibration, general engineering, renovation works and general contracts.
                </p>
                <p>
                  Also engaged in Real estate development, general equipments, merchants, land developers, 
                  commission agents and other business.
                </p>
                <p>
                  The company has grown over the past years in the delivering of excellent services in 
                  construction and plumbing industry to its current level and is now operating within Uganda 
                  and East Africa. Our head offices are located at Kansanga, Ggaba Rd, Near KIU World Saloon Building,
                  Basement Floor Room B05, P.O Box 148303, Kampala, Uganda.
                </p>
                <p>
                  Delivering a consistent and reliable service door to door within transit time to our clients has been 
                  one of our most fundamental objectives since the establishment of the company. Honesty and integrity 
                  is our company's important principal which has contributed much to our growth and trust to its long served clients.
                </p>
                <p>
                  Our team can also operate locally (within the country borders) and even outside the entire parts of the country 
                  and within East Africa. If you have been longing for that dedicated and efficient service with integrity, we are here for you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="bg-gradient-to-r from-primary-50 to-blue-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-black text-gray-900 mb-6">
              <span className="text-primary">Our</span> <span className="text-gray-600">History</span>
            </h2>
            <div className="prose prose-lg text-gray-600">
              <p className="mb-4">
                It started way back in 2007 by Mr. Mabirizi Peter and Sekitooleko Andrew who thought of it 
                in just words and later into a proposal to put it into consideration and taken to the 
                registrar of companies so as to register it and it becomes known to the business work on the 
                <strong> 05th day of February 2013</strong> with Incorporation certificate number <strong>162311</strong> and 
                TIN number <strong>1003000812</strong> where it would start competing for serious jobs in the field of 
                plumbing, engineering & construction works and general contracts.
              </p>
              <p className="mb-4">
                As time went on the idea was being forwarded whenever we were meeting and by the end of September 2008 
                we had already secured space where official work was to be carried out and by the end of 2008 all the 
                required materials needed to operate the plumbing and civil construction works. We kept on moving with 
                our dream slowly but surely while operating at low ground levels in the neighborhoods of Kansanga, Bunga, 
                Buziga, Munyonyo, Makindye, Muyenga, Kabalagala, Nsambya where we were working mainly.
              </p>
              <p className="mb-4">
                The start of 2009 came were we had at least reached to certain achievements and the market outside was 
                growing to an extent that more clients wanted our services we continued with our journey and we had to 
                purchase the required office tools and materials.
              </p>
              <p className="mb-4">
                Today the company employees over 36 people locally. PLENCO has expanded its services to 
                neighboring countries like Rwanda, South Sudan and Tanzania. As PLENCO is making over 10 years 
                in business, we can look back up on a solid foundation as we strive for future success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-center mb-10">
            <span className="text-primary">Our Core</span> <span className="text-gray-600">Values</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-primary">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Our Mission</h3>
              <p className="text-gray-600">
                To provide complete solutions in construction and plumbing that are efficient, reliable, and affordable.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-blue-500">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Our Vision</h3>
              <p className="text-gray-600">
                To achieve and maintain the highest level of professionalism and efficiency in our industry.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-green-500">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Our Approach</h3>
              <p className="text-gray-600">
                Maintain a high level of recognition and respect from clients through expert service.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-orange-500">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Our Values</h3>
              <p className="text-gray-600">
                Integrity, transparency, accountability and discipline in all we do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-gradient-to-r from-primary to-primary-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black mb-8 text-center">
            <span className="text-white">Why Choose</span> <span className="text-primary-200">Plenco?</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              'Total control over all aspects of your projects',
              'Collaboration rather than confrontation',
              'Professional construction input during design work',
              'Effective delivery of high quality works',
              'Careful attention to design integrity',
              'Minimal overall project time',
              'Cost commitment early in design phase',
              'Continuous review of construction documents',
              'Expert team with proven track record'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-all">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white text-primary flex items-center justify-center text-sm font-bold mt-0.5">
                  ✓
                </div>
                <p className="text-white font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info Sections */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
            <div>
              <h2 className="text-3xl font-black mb-4">
                <span className="text-primary">Diverse</span> <span className="text-gray-600">Markets</span>
              </h2>
              <p className="text-gray-600 mb-4">
                We are able to work with private and public markets such as residences, schools and health facilities, 
                small or large companies whether technically challenging or not.
              </p>
              <p className="text-gray-600">
                We carried out major renovations on numerous projects; electrical overhauling, High voltage and low voltage 
                line construction, Transformer installation, maintenance/Servicing, Generator installation/Servicing, Air 
                conditioner maintenance, plumbing works, calibration works, roof repairs, carpentry refurbishment and works 
                on wood and painting among others.
              </p>
            </div>
            <div>
              <h2 className="text-3xl font-black mb-4">
                <span className="text-primary">Our</span> <span className="text-gray-600">Involvement</span>
              </h2>
              <p className="text-gray-600">
                <strong>PLENCO Plumbing and Construction Co. Ltd</strong> gets involved in early preparations of projects 
                such as the designing, project cost estimates. This therefore enables us to offer suggestions to the clients 
                early enough, hence production of value for money works and also avoidance of misunderstandings between the two parties.
              </p>
            </div>
            <div>
              <h2 className="text-3xl font-black mb-4">
                <span className="text-primary">Value</span> <span className="text-gray-600">-Led</span>
              </h2>
              <p className="text-gray-600">
                Being "value-led" is the heart of <strong>PLENCO</strong>'s success. Some of the Company's principal values 
                is putting clients' interests first, keeping commitments made, making sure everyone wins, valuing and respecting 
                others, doing it the safe way, have built our company's ever growing reputation.
              </p>
            </div>
            <div>
              <h2 className="text-3xl font-black mb-4">
                <span className="text-primary">Office</span> <span className="text-gray-600">Hours</span>
              </h2>
              <p className="text-gray-600 mb-4">
                We are open every week Monday to Friday from 8 am to 6 pm, on Saturdays from 9:00 am to 3:00 pm. We are closed 
                on Sundays and Public holidays but can always contact us 24/7 on the phone line in case you need our Services.
              </p>
              <p className="text-gray-600 font-medium">
                We are looking forward to doing business with you now and in future and providing you with the best reliable 
                and top quality goods and services at all times.
              </p>
            </div>
          </div>

          {/* Teamwork Section */}
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-8 rounded-lg mb-12">
            <h2 className="text-3xl font-black mb-6 text-center">
              <span className="text-primary">Team</span><span className="text-gray-600">work</span>
            </h2>
            <div className="space-y-4 text-gray-600">
              <p>
                Our nature of work is highly depending on teams and our success is built on the strong teamwork we have 
                exhibited on every one of our projects. We employ a high standard of experienced personnel at each level 
                and in every department. Our modern management approach to human resource ensures that we have the best 
                trained and experienced personnel.
              </p>
              <p>
                We greatly believe that each team member; the client, design professional and construction specialist, 
                each brings a specific ideal that strengthen the project team. We stress methods that base on these strengths, 
                enabling all members to work closely and efficiently as a team specifically designed to deliver on the project. 
                The advantage of this depends on the trust and co-operation between owner, architect and constructor.
              </p>
            </div>
          </div>

          {/* Code of Conduct Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-black mb-4">
                <span className="text-primary">Code of Business</span> <span className="text-gray-600">Conduct</span>
              </h2>
              <p className="text-gray-600 mb-4">
                The code of business conduct of PLENCO consists of the policies relating to the ethical and legal standards 
                of conduct to be followed by employees and agents of the company in the conduct of its business.
              </p>
              <p className="text-gray-600">
                To report a violation of the law or the code of business u may utilize the PLENCO ethics hotline and reporting 
                system at PLENCO point. Available 12hours a day, 6days a week, your questions will be addressed by the company 
                confidently and discretely. You may remain anonymous and translators are available.
              </p>
            </div>
            <div>
              <h2 className="text-3xl font-black mb-4">
                <span className="text-primary">Why</span> <span className="text-gray-600">Us?</span>
              </h2>
              <p className="text-gray-600">
                We offer a convenient menu of standard construction, plumbing, calibration works, tank fabrication and other 
                designs, general engineering contracts and real estate management services, including general equipment's, 
                merchants, land developers, commission agents and other business to balance urgency and cost-effectiveness for 
                your day-to-day construction. Some of the factors that have helped us to evolve as the preferred general 
                construction and engineering solution provider.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Map Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-black text-gray-900 mb-2">
              <span className="text-primary">Our</span> <span className="text-gray-600">Location</span>
            </h2>
            <p className="text-gray-600 text-lg">
              Visit us at KIU World Saloon Building, Basement Floor Room B05
            </p>
            <p className="text-gray-600">
              Kansanga, Ggaba Rd, P.O Box 148303, Kampala, Uganda
            </p>
          </div>

          {/* Contact Information Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Head Office */}
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg mb-4">
                <MapPinIcon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Head Office</h3>
              <p className="text-gray-600 text-sm">
                P.O.Box 148303<br />
                Kansanga<br />
                Kampala, Uganda
              </p>
            </div>

            {/* Phone */}
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
                <PhoneIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Contact Us</h3>
              <a href="tel:+256200906397" className="text-primary hover:underline text-sm font-semibold block">
                +256 200 906397 | +256 704 485986 | +256 786 272003
              </a>
            </div>

            {/* Email */}
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4">
                <EnvelopeIcon className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Email Us</h3>
              <a href="mailto:info@plencoplumbingltd.com" className="text-primary hover:underline text-sm block mb-1">
                info@plencoplumbingltd.com
              </a>
              <a href="mailto:plencoplumbing@gmail.com" className="text-primary hover:underline text-sm block">
                plencoplumbing@gmail.com
              </a>
            </div>

            {/* Working Hours */}
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg mb-4">
                <ClockIcon className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Working Time</h3>
              <p className="text-gray-600 text-sm">
                Mon - Fri<br />
                8:00AM - 6:00PM<br />
                <span className="mt-1 block">Sat: 9:00AM - 3:00PM</span>
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <iframe
              src="https://maps.google.com/maps?q=0.2944772,32.6046877&z=17&output=embed"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
